import { borderRadius, color, focusRing, snap, typeStyle, units } from '@m/alchemy-ui/theme';
import { Button } from '@m/alchemy-ui/Button';
import { ButtonGroup } from '@m/alchemy-ui/ButtonGroup';
import { Card } from '@m/alchemy-ui/Card';
import { CardHeader } from '@m/alchemy-ui/CardHeader';
import { LoadingIndicator } from '@m/alchemy-ui/LoadingIndicator';
import styled, { css } from 'styled-components';
import { DeleteIconButton } from '../../common/components';
import { Breakpoints } from '../../common/enums';

export const Wrapper = styled.div`
  width: 100%;
  max-width: ${snap(976)};
  background: white;
  padding-top: ${units(2)};
  min-height: 350px;
  display: flex;
  flex-direction: column;
`;

export const IntroText = styled.p`
  ${typeStyle('bodyS')};
  margin: 0 0 ${units(2)};
`;

export const ContentRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${units(4)};
  margin-bottom: ${units(1)};

  @media (max-width: 1024px) {
    gap: ${units(2)};
  }
`;

export const TitleColumn = styled.div`
  flex: 1 1 min(400px, 100%);
  max-width: ${snap(400)};
`;

export const CheckboxColumn = styled.div`
  margin-top: ${units(4)};
  white-space: nowrap;

  @media (max-width: 768px) {
    margin-top: 0;
    width: 100%;
  }
`;

export const Actions = styled.div`
  margin-top: ${units(2)};
  display: flex;
  justify-content: flex-end;
`;

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${units(2)};
  margin: ${units(2)} 0 ${units(3)};
`;

export const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
`;

export const ToolbarCenter = styled.div`
  display: flex;
  align-items: center;
  gap: ${units(1)};
`;

export const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${units(1)};

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

export const PreviewText = styled.span`
  ${typeStyle('bodyM')};
`;

export const PreviewButtonGroup = styled(ButtonGroup)`
  > div {
    margin-bottom: 0;
  }

  svg {
    display: block;
    transform: translateY(${units(0.5)});
  }
`;

export const AddElementMenu = styled.div`
  display: flex;
  flex-direction: column;
`;

export const SecondaryButton = styled(Button)`
  &:not([disabled]):hover {
    color: ${color('base', { palette: 'interactiveConstructive' })};
  }
`;

export const AddElementTriggerContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${units(1)};
`;

export const AddElementHint = styled.p`
  ${typeStyle('bodyS')};
  margin: 0 0 ${units(0.5)};
  padding: ${units(0.5)} ${units(1)};
  background: ${color('container')};
`;

export const AddElementItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${units(1)};
  width: 100%;
  padding: ${units(0.5)} ${units(1)};
  border: none;
  background: transparent;
  text-align: left;
  cursor: move;
  ${typeStyle('bodyM')};
  font-weight: normal;

  &:hover {
    background: ${color('container')};
  }

  /* Inner ring: the menu's scrolling PopperBox would clip an outer one. */
  &:focus-visible {
    background: ${color('container')};
    ${focusRing({ type: 'inner' })};
  }
`;

export const AddElementItemIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${units(3)};
  height: ${units(3)};
`;

export const LineDash = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${units(3)};
`;

export const BannerRow = styled.div`
  margin-top: ${units(3)};
  display: flex;
  flex-direction: column;
  gap: ${units(2)};
`;

export const BannerUploadGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(1)};
`;

export const DesignTabInfo = styled.p`
  ${typeStyle('bodyS')};
  margin: ${units(3)} 0 0;
`;

export const DesignTabLink = styled.a`
  color: ${color('typeLink')};
`;

export const GridArea = styled.div`
  margin-top: ${units(3)};
`;

export const DropZoneRow = styled.div<{ $isDragActive?: boolean; $isDraggedOver?: boolean }>`
  min-height: ${snap(40)};
  margin: ${units(1)} 0;
  border-radius: ${borderRadius('large')};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${units(1.5)};
  background: #fafafa;
  border: 1px dashed transparent;

  ${({ $isDragActive }) =>
    $isDragActive &&
    css`
      border-color: ${color('base', { palette: 'interactiveConstructive' })};
      background: ${color('hint', { palette: 'interactiveConstructive' })};
    `};

  ${({ $isDraggedOver }) =>
    $isDraggedOver &&
    css`
      background: ${color('highlight', { palette: 'interactiveConstructive' })};
    `};
`;

export const DropZoneText = styled.p<{ $isDragActive?: boolean }>`
  ${typeStyle('headerS')};
  color: ${({ $isDragActive }) => ($isDragActive ? color('typeMeta') : '#cbcbcb')};
  margin: 0;
  text-align: center;
`;

export const PanelGrid = styled.div`
  max-width: ${snap(976)};
  min-width: ${snap(Breakpoints.TABLET_MIN)};
`;

export const PanelRow = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  gap: ${units(2)};
`;

/*
 * Flex-grow ratios keep row proportions while the viewport shrinks:
 * 3 panels → 1:1:1 (~33% each); 2 auto panels → 50/50; wide + auto → 2:1 (66.6/33.3).
 */
export const PanelItemWrapper = styled.div<{ $isWide: boolean; $isDragging: boolean }>`
  position: relative;
  display: flex;
  flex: ${({ $isWide }) => ($isWide ? 2 : 1)} 1 0;
  min-width: 0;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.4 : 1)};
`;

export const PanelCard = styled(Card)<{ $hasError?: boolean; $isDropPreviewIncoming?: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  ${({ $hasError }) =>
    $hasError &&
    css`
      border-color: ${color('base', { palette: 'expressiveNegative' })};
    `}

  ${({ $isDropPreviewIncoming }) =>
    $isDropPreviewIncoming &&
    css`
      border-color: ${color('base', { palette: 'interactiveConstructive' })};
      background: ${color('hint', { palette: 'interactiveConstructive' })};
      box-shadow: inset 0 0 0 2px ${color('base', { palette: 'interactiveConstructive' })};
    `}
`;

export const PanelDropPreviewOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 2;
  display: flex;
  align-items: stretch;
  gap: ${units(2)};
  min-height: 100%;
  height: max-content;
  background: white;
  pointer-events: none;
`;

/*
 * Custom children layout instead of CardHeader's title/endItemsFixed props, so the drag icon,
 * title and kebab menu sit on one vertically-centered line.
 */
export const PanelCardHeader = styled(CardHeader)`
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
`;

export const PanelHeaderTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${units(1)};
  min-width: 0;
  ${typeStyle('headerS')};
  color: ${color('contrast', { palette: 'illustrativeSteelGrey' })};
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

/* The visual pointer-drag affordance. Non-drag placement is available from the panel options menu. */
export const PanelDragHandle = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  padding: 2px;
  border-radius: ${borderRadius()};
`;

export const PanelInfoText = styled.p`
  ${typeStyle('bodyS')};
  color: ${color('typeMeta')};
  margin: 0;
`;

export const PanelLine = styled.hr`
  width: 100%;
  margin: 0;
  border: 0;
  border-top: 1px solid ${color('line')};
`;

export const PanelSizeMenuSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(0.5)};
  padding: ${units(1.5)} ${units(1.5)} ${units(1)};
`;

export const PanelSizeMenuLabel = styled.span`
  ${typeStyle('bodyS')};
  font-weight: bold;
`;

export const PanelEditorSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${units(1)};
  width: 100%;
  max-width: ${snap(400)};

  & + & {
    margin-top: ${units(2)};
  }
`;

export const PanelEditorLabel = styled.span`
  ${typeStyle('bodyS')};
  font-weight: bold;
`;

export const PanelErrorStatus = styled.div`
  margin-bottom: ${units(1)};
`;

// For editors whose main content should fill the panel width (rich text editor, previews, uploads)
// instead of the 400px form-control cap.
export const PanelEditorStretchSection = styled(PanelEditorSection)`
  align-items: stretch;
  max-width: none;
`;

// Shrinks to the image so the delete button can anchor to its bottom-right corner.
export const PanelImagePreviewWrapper = styled.div`
  position: relative;
  align-self: center;
  max-width: 100%;
`;

export const PanelImagePreview = styled.img<{ $width?: string | null; $rounded: boolean }>`
  display: block;
  width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
  max-width: 100%;
  border-radius: ${({ $rounded }) => ($rounded ? '8px' : '0')};
`;

// Same affordance as the banner upload preview (SingleUpload's overlaid delete button).
export const PanelImageDeleteButton = styled(DeleteIconButton)`
  position: absolute;
  bottom: ${units(1)};
  right: ${units(1)};
  margin: 0;
`;

export const VideoPreview = styled.div`
  width: 100%;

  iframe,
  video {
    max-width: 100%;
    border: 0;
  }
`;

export const ErrorBannerRow = styled.div`
  margin-bottom: ${units(2)};
`;

export const DeletePageRow = styled.div`
  margin-top: ${units(1)};
  display: flex;
  justify-content: flex-end;
`;

export const StyledLoadingIndicator = styled(LoadingIndicator)`
  margin-top: ${units(4.75)};
`;
